/* eslint-disable react/no-multi-comp */
import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  styled,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import {
  defaultFocusOutline,
  handleKeyEvent,
} from "../../../helpers/accessibility";
import type { CheckboxTreeProps } from ".";
import { Option as FilterOption, OptionValue } from "../Filter";
import { SeoAnchor } from "../anchor/SeoAnchor";
import { useTranslation } from "../../../helpers/i18n";

interface Props extends CheckboxTreeProps {
  option: FilterOption;
  seoHrefPrefix: string;
}

const Label = styled((props: TypographyProps) => (
  <Typography component="span" {...props} />
))(({ theme }) => ({
  padding: ".3rem",
  minWidth: "2rem",
  textAlign: "center",
  borderRadius: ".5rem",
  fontWeight: "bold",
  fontSize: `${theme.typography.body2.fontSize} !important`,
  margin: ".2rem",
  background: theme.palette.inputs.default,
  border: `1px solid ${theme.palette.inputsUnderline.main}`,
  color: theme.palette.text.inputDefault,
  flex: "initial !important",
}));

const expandContainerWidth = 2;

const SubLabel = styled(Label)(({ theme }) => ({
  marginRight: `${expandContainerWidth + 0.2}rem`,
  color: theme.palette.text.inputDefault,
}));

const ExpandIconButton = styled(IconButton)<{ invisible: boolean }>(
  ({ invisible, theme }) => ({
    width: `${expandContainerWidth}rem`,
    height: `${expandContainerWidth}rem`,
    visibility: invisible ? "hidden" : "initial",
    display: "flex",
    alignItems: "center",
    "&:focus-visible": {
      outline: defaultFocusOutline(theme),
      outlineOffset: -3,
    },
  }),
);

const ExpandIcon = styled("div")(({ theme }) => ({
  marginTop: ".4rem",
  color: theme.palette.text.default,
}));

const ControlContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  "& .MuiFormControlLabel-root": {
    width: "100%",
  },
}));

const StyledSeoAnchor = styled(SeoAnchor)(({ theme }) => ({
  flex: 1,
  "&:focus": {
    outline: 0,
  },
  "&:focus-visible": {
    borderRadius: theme.shape.borderRadius,
    outline: defaultFocusOutline(theme),
    outlineOffset: -3,
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  width: "100%",
  color: theme.palette.text.default,
  "& .MuiTypography-root": {
    lineHeight: 1.1,
    fontSize: theme.typography.body1.fontSize,
    flex: 1,
  },
  wordBreak: "break-word",
}));

const StyledFormControlLabelSubOption = styled(StyledFormControlLabel)(
  ({ theme }) => ({
    "& .MuiTypography-root": {
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.default,
    },
  }),
);

const StyledCollapse = styled(Collapse)(() => ({
  overflow: "hidden",
}));

const StyledCheckBox = styled(Checkbox)(({ theme }) => ({
  paddingTop: `${0} !important`,
  paddingBottom: `${0} !important`,
  color: theme.palette.text.default,
  marginLeft: 3,
}));

const SubOptionsList = styled(List)(() => ({ paddingLeft: "1rem" }));

interface OptionProps {
  handleOnChangeAll: () => void;
  detailsLabel?: string;
  label: string;
  value: string;
  allChecked: boolean;
  indeterminate: boolean;
}

const Option = ({
  handleOnChangeAll,
  detailsLabel,
  label,
  value,
  allChecked,
  indeterminate,
}: OptionProps) => (
  <StyledFormControlLabel
    key={value}
    onClick={handleOnChangeAll}
    control={
      <StyledCheckBox
        indeterminate={indeterminate}
        checked={allChecked}
        onChange={handleOnChangeAll}
      />
    }
    label={
      <ControlContainer>
        {label}
        {detailsLabel && <Label>{detailsLabel}</Label>}
      </ControlContainer>
    }
  />
);

const CheckboxGroup: FunctionComponent<Props> = ({
  option: { label, value, subOptions, detailsLabel },
  onChange,
  defaultCollapsed,
  seoHrefPrefix,
  initialSelectedOptionValues = [],
}: Props) => {
  const haveSubOptions = !!subOptions?.length;
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  const subOptionsValues = useMemo(
    () => subOptions?.map(({ value }) => value) || [],
    [subOptions],
  );

  const indeterminate = useMemo(
    () =>
      subOptionsValues?.some((value) =>
        initialSelectedOptionValues.includes(value),
      ),
    [initialSelectedOptionValues, subOptionsValues],
  );

  const getToggledOptionValues = (values: OptionValue[], value: OptionValue) =>
    values.includes(value)
      ? values.filter((currentValue) => currentValue !== value)
      : [...values, value];

  const handleOnChange = useCallback(
    (optionValue: OptionValue) => {
      if (haveSubOptions) {
        const newValues = getToggledOptionValues(
          initialSelectedOptionValues,
          optionValue,
        ).filter((optionValue) => optionValue != value);

        onChange(newValues, []);
      } else {
        const newValues = getToggledOptionValues(
          initialSelectedOptionValues,
          value,
        );
        onChange(newValues, []);
      }
    },
    [onChange, getToggledOptionValues, haveSubOptions, value],
  );

  const allChecked = initialSelectedOptionValues.includes(value);

  const handleOnChangeAll = () => {
    if (allChecked) {
      onChange([], []);
    } else {
      onChange([value, ...subOptionsValues], []);
    }
  };

  const handleChange = useCallback(
    (e: Pick<Event, "preventDefault" | "stopPropagation">) => {
      e.preventDefault();
      e.stopPropagation();
      handleOnChangeAll();
    },
    [handleOnChangeAll],
  );

  const handleExpanded = useCallback(() => {
    setExpanded((expanded) => !expanded);
  }, []);

  const theme = useTheme();
  const { t } = useTranslation();
  const ArrowDownIcon = theme.icons.arrowDown;
  const ArrowUpIcon = styled(ArrowDownIcon)(() => ({
    transform: "rotate(180deg)",
  }));

  return (
    <>
      <ControlContainer>
        <StyledSeoAnchor
          href={`${seoHrefPrefix}${value}`}
          onKeyDown={handleKeyEvent({
            onEnter: handleChange,
          })}
          tabIndex={-1}
        >
          <Option
            allChecked={allChecked}
            handleOnChangeAll={handleOnChangeAll}
            indeterminate={!allChecked && indeterminate}
            label={label}
            value={value}
            detailsLabel={detailsLabel}
          />
        </StyledSeoAnchor>

        <ExpandIconButton
          invisible={!haveSubOptions}
          disableRipple
          aria-hidden={!haveSubOptions}
          onClick={handleExpanded}
          aria-label={t(
            expanded
              ? "accessibility_filters_see_less"
              : "accessibility_filters_see_more",
          )}
          {...(haveSubOptions && {
            "aria-expanded": expanded,
          })}
        >
          <ExpandIcon>
            {expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </ExpandIcon>
        </ExpandIconButton>
      </ControlContainer>
      {haveSubOptions && (
        <StyledCollapse in={expanded} timeout="auto" unmountOnExit>
          <SubOptionsList disablePadding>
            {subOptions?.map(
              ({
                label: subLabel,
                value: subValue,
                detailsLabel: subDetailsLabel,
              }) => (
                <StyledFormControlLabelSubOption
                  key={subValue}
                  onKeyDown={handleKeyEvent({
                    onEnter: handleChange,
                  })}
                  control={
                    <StyledCheckBox
                      checked={initialSelectedOptionValues.includes(subValue)}
                      onChange={() => handleOnChange(subValue)}
                    />
                  }
                  label={
                    <ControlContainer>
                      {subLabel}
                      {subDetailsLabel && (
                        <SubLabel>{subDetailsLabel}</SubLabel>
                      )}
                    </ControlContainer>
                  }
                />
              ),
            )}
          </SubOptionsList>
        </StyledCollapse>
      )}
    </>
  );
};

export default CheckboxGroup;
